import { useState, useEffect } from 'react';
import { Clock, Calendar } from 'lucide-react';

import { getStaticData } from '../hooks/useStaticData';
import { apiService } from '../services/api';

import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const ScheduleSection = () => {
  // Use static data as initial state to avoid flash of default content
  const staticSchedule = getStaticData('schedule');
  // Convert schedule object to array format if needed
  const initialSchedule = staticSchedule ?
    (Array.isArray(staticSchedule) ? staticSchedule : Object.values(staticSchedule)) : [];

  const [schedule, setSchedule] = useState(initialSchedule);
  const [programs, setPrograms] = useState(() => getStaticData('programs') || []);
  const [businessInfo, setBusinessInfo] = useState(() => getStaticData('businessInfo'));
  const [loading, setLoading] = useState(() => {
    // Only show loading if we don't have static data
    return !staticSchedule || initialSchedule.length === 0;
  });

  const today = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const todayKey = days[today];
  const tomorrowKey = days[(today + 1) % 7];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scheduleData, businessData, programsData] = await Promise.all([
          apiService.getSchedule(),
          apiService.getBusinessInfo(),
          apiService.getPrograms()
        ]);
        if (scheduleData) setSchedule(scheduleData);
        if (businessData) setBusinessInfo(businessData);
        if (programsData) setPrograms(programsData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get visible program IDs (programs returned from public API are already filtered to visible=true)
  const visibleProgramIds = programs.map(p => p.id);

  // Filter out hidden programs and sort chronologically
  const getFilteredAndSortedClasses = (dayKey) => {
    const daySchedule = schedule.find(s => s.day === dayKey);
    if (!daySchedule || !daySchedule.time_slots) return [];

    return daySchedule.time_slots
      .filter(slot => {
        // If slot has a program_id, check if it's in the visible programs list
        // If no program_id, show the slot (it's a custom class without a linked program)
        if (!slot.program_id) return true;
        return visibleProgramIds.includes(slot.program_id);
      })
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const getTodayClasses = () => getFilteredAndSortedClasses(todayKey);
  const getTomorrowClasses = () => getFilteredAndSortedClasses(tomorrowKey);

  const handleViewFullSchedule = () => {
    const email = businessInfo?.email || 'info@owazamartialarts.com';
    const subject = encodeURIComponent('View Full Schedule Request');
    const body = encodeURIComponent('Hi, I would like to see the full class schedule and discuss available times. Thank you!');
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const hourNum = parseInt(hour, 10);
    const hour12 = hourNum === 0 ? 12 : (hourNum > 12 ? hourNum - 12 : hourNum);
    const ampm = hourNum >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minute} ${ampm}`;
  };

  const renderClasses = (classes, dayName) => {
    if (!classes || classes.length === 0) {
      return <p className="text-gray-500 text-sm">No classes scheduled</p>;
    }

    return (
      <div className="space-y-3">
        {classes.map((slot, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-semibold text-gray-900">{slot.program_name || 'Class'}</h4>
              {slot.note && (
                <p className="text-sm text-gray-600">{slot.note}</p>
              )}
            </div>
            {slot.start_time && slot.end_time && (
              <Badge variant="outline" className="text-xs">
                {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
              </Badge>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return <div>Loading schedule...</div>;
  }

  return (
    <section id="schedule" className="py-20 bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            This Week's Schedule
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Appointment-only training ensures focused attention and maximum progress. 
            Book your session today!
          </p>
        </div>

        {/* Today & Tomorrow */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
          <Card className="shadow-lg border-0">
            <CardHeader className="bg-red-600 text-white">
              <h3 className="flex items-center space-x-2 font-semibold">
                <Calendar className="h-5 w-5" />
                <span>Today - {dayNames[today]}</span>
              </h3>
            </CardHeader>
            <CardContent className="p-6">
              {renderClasses(getTodayClasses(), dayNames[today])}
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader className="bg-blue-600 text-white">
              <h3 className="flex items-center space-x-2 font-semibold">
                <Clock className="h-5 w-5" />
                <span>Tomorrow - {dayNames[(today + 1) % 7]}</span>
              </h3>
            </CardHeader>
            <CardContent className="p-6">
              {renderClasses(getTomorrowClasses(), dayNames[(today + 1) % 7])}
            </CardContent>
          </Card>
        </div>

        {/* Schedule Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 max-w-4xl mx-auto rounded-r-lg mb-8">
          <div className="flex items-start">
            <div className="ml-3">
              <p className="text-yellow-800">
                <strong>Notice:</strong> Appointment-only studio • No class observation • 
                Members book via portal • Guardians may wait in lobby
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            size="lg" 
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-4"
            onClick={handleViewFullSchedule}
          >
            View Full Schedule & Book
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ScheduleSection;